import React, { useEffect, useState } from "react";
import { Container } from "react-bootstrap";
import { Helmet } from "react-helmet-async";
import LoadingState from "../../components/common/LoadingState";
import { useDispatch, useSelector } from "react-redux";
import axios from "axios";
import { isEmpty } from "lodash";

// Content
import PageContent from "./PageContent";

// Redux
import {
  setSelectYear,
  setSelectMonth,
  setIsLoading,
} from "../../redux/slices/dashboard/dashboardSlice";
import {
  getFinancialReport,
  getFinancialReportByMinistries,
  getFinancialReportByDepartments,
  getOverallExpenditure,
  getProjectsProgressStatus,
  getProjects,
  getFinancialReportByPrograms,
  getFinancialReportByMainProjects,
  getFinancialReportByBranches,
} from "../../redux/slices/dashboard/overallReportSlice";
import {
  getCombineReportProjectsProgressStatus,
  getCombineReportProgressStatusByMinistries,
  getCombineReportProgressStatusByDepartments,
  getCombineReportProgressStatusByPrograms,
  getCombineReportProgressStatusByMainProjects,
} from "../../redux/slices/dashboard/combineReportSlice";
import { getRmkItems } from "../../redux/slices/utilsSlice";

const MainPage = () => {
  const dispatch = useDispatch();

  const [yearOptions, setYearOptions] = useState([]);

  const [rmkValue, setRmkValue] = useState(null);

  const {
    isLoading,
    user,
    currentRmk,
    selectYear,
    selectMonth,
    selectMinistry,
    selectDepartment,
    selectBranch,
    selectProgram,
    navFilter,
    searchProgressStatus,
  } = useSelector((state) => ({
    isLoading: state.rmkReducer.isLoading,
    currentRmk: state.rmkReducer.activeRmk,
    user: state.userReducer.user,
    selectYear: state.dashboardReducer.selectYear,
    selectMonth: state.dashboardReducer.selectMonth,
    selectMinistry: state.dashboardReducer.selectMinistry,
    selectDepartment: state.dashboardReducer.selectDepartment,
    selectBranch: state.dashboardReducer.selectBranch,
    selectProgram: state.dashboardReducer.selectProgram,
    navFilter: state.dashboardReducer.navFilter,
    searchProgressStatus: state.dashboardReducer.searchProgressStatus,
  }));

  const handleChangeRmk = (value) => {
    setYearOptions(null);

    const { first_year, second_year, third_year, fourth_year, fifth_year } =
      value;

    const rmkYears = [first_year, second_year, third_year, fourth_year, fifth_year];
    const currentYear = new Date().getFullYear();
    
    // Determine the default year: use current year if within RMK range, otherwise use the latest year in RMK
    const defaultYear = rmkYears.includes(currentYear) ? currentYear : fifth_year;

    const newArray = [
      {
        label: fifth_year,
        value: fifth_year,
      },
      {
        label: fourth_year,
        value: fourth_year,
      },
      {
        label: third_year,
        value: third_year,
      },
      {
        label: second_year,
        value: second_year,
      },
      {
        label: first_year,
        value: first_year,
      },
    ];

    setYearOptions(newArray);
    setRmkValue(value);
    
    // Set the year to a valid year within the RMK range
    dispatch(setSelectYear({
      label: defaultYear,
      value: defaultYear,
    }));
  };

  useEffect(() => {
    let source = axios.CancelToken.source();

    const props = {
      source: source,
      params: {
        rmk_id: rmkValue?.number,
        year: selectYear?.value,
        month: selectMonth?.value,
        ministry_id: selectMinistry,
        department_id: selectDepartment,
        branch_id: selectBranch,
        program_id: selectProgram,
      },
    };

    const initialize = async () => {
      dispatch(setIsLoading(true));
      dispatch(getFinancialReport(props));
      dispatch(getOverallExpenditure(props));

      if (navFilter === "ministry") {
        dispatch(getFinancialReportByMinistries(props));
      }
      if (navFilter === "department") {
        dispatch(getFinancialReportByDepartments(props));
      }
      if (navFilter === "branch") {
        dispatch(getFinancialReportByBranches(props));
      }
      if (navFilter === "program") {
        dispatch(getFinancialReportByPrograms(props));
      }
      if (navFilter === "main-project") {
        dispatch(getFinancialReportByMainProjects(props));
      }

      await Promise.all([
        dispatch(getProjectsProgressStatus(props)),
        dispatch(getCombineReportProjectsProgressStatus(props)),
        dispatch(getCombineReportProgressStatusByMinistries(props)),
        dispatch(getCombineReportProgressStatusByDepartments(props)),
        dispatch(getCombineReportProgressStatusByPrograms(props)),
        dispatch(getCombineReportProgressStatusByMainProjects(props)),
      ]);

      dispatch(setIsLoading(false));
    };

    if (!isEmpty(rmkValue)) {
      initialize();
    }

    return () => {
      source.cancel();
    };
  }, [dispatch, rmkValue, selectYear, selectMonth, navFilter]);

  useEffect(() => {
    let source = axios.CancelToken.source();

    const props = {
      source: source,
      params: {
        rmk_id: rmkValue?.number,
        year: selectYear?.value,
        month: selectMonth?.value,
        ministry_id: selectMinistry,
        department_id: selectDepartment,
        branch_id: selectBranch,
        program_id: selectProgram,
        status_code: searchProgressStatus,
      },
    };

    const initialize = async () => {
      await dispatch(getProjects(props));
    };

    if (!isEmpty(rmkValue)) {
      initialize();
    }

    return () => {
      source.cancel();
    };
  }, [dispatch, rmkValue, selectYear, selectMonth, navFilter, searchProgressStatus]);

  useEffect(() => {
    if (!isEmpty(currentRmk)) {
      setRmkValue(currentRmk);
      const { first_year, second_year, third_year, fourth_year, fifth_year } =
        currentRmk;

      const rmkYears = [first_year, second_year, third_year, fourth_year, fifth_year];
      const currentYear = new Date().getFullYear();
      
      // Determine the default year: use current year if within RMK range, otherwise use the latest year in RMK
      const defaultYear = rmkYears.includes(currentYear) ? currentYear : fifth_year;

      const newArray = [
        {
          label: fifth_year,
          value: fifth_year,
        },
        {
          label: fourth_year,
          value: fourth_year,
        },
        {
          label: third_year,
          value: third_year,
        },
        {
          label: second_year,
          value: second_year,
        },
        {
          label: first_year,
          value: first_year,
        },
      ];

      setYearOptions(newArray);
      
      // Set the year to a valid year within the RMK range
      dispatch(setSelectYear({
        label: defaultYear,
        value: defaultYear,
      }));
    }
  }, [currentRmk, dispatch]);

  useEffect(() => {
    let source = axios.CancelToken.source();

    const props = {
      source: source,
    };

    dispatch(getRmkItems(props));

    return () => {
      source.cancel();
    };
  }, [dispatch]);

  return (
    <React.Fragment>
      <Helmet title='Dashboard' />

      <Container fluid className='p-0'>
        {isLoading ? (
          <LoadingState />
        ) : (
          <PageContent
            changeRmk={handleChangeRmk}
            rmkValue={rmkValue}
            yearOptions={yearOptions}
          />
        )}
      </Container>
    </React.Fragment>
  );
};

export default MainPage;
